import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FeedbackThread from 'features/feedback/FeedbackThread';
import {
  getFeedbackTickets,
  replyToFeedbackTicket,
  updateFeedbackTicket,
  getFeedbackScreenshotUrl,
  type FeedbackTicket,
  type FeedbackStatus,
  type FeedbackPriority,
} from 'API/feedback';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import { useUserData } from 'hooks/use-user-data';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Roles } from '../enums';

const AdminFeedbackTicketsPage: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { role } = useUserData();
  const [feedbackTickets, setFeedbackTickets] = useState<FeedbackTicket[]>([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState<FeedbackStatus | ''>('');
  const [feedbackPriorityFilter, setFeedbackPriorityFilter] = useState<FeedbackPriority | ''>('');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState<FeedbackTicket | null>(null);
  const [ticketAdminNote, setTicketAdminNote] = useState('');
  const [ticketStatus, setTicketStatus] = useState<FeedbackStatus>('open');
  const [ticketPriority, setTicketPriority] = useState<FeedbackPriority>('medium');
  const [savingTicket, setSavingTicket] = useState(false);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  const fetchFeedbackTickets = useCallback(async () => {
    setLoadingFeedback(true);
    setFeedbackError(null);
    try {
      const result = await getFeedbackTickets({
        status: feedbackStatusFilter || undefined,
        priority: feedbackPriorityFilter || undefined,
        search: feedbackSearch || undefined,
        page: feedbackPage,
        limit: 20,
      });
      setFeedbackTickets(result.items);
      setFeedbackTotal(result.total);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setFeedbackError(error?.message || 'Could not load feedback tickets');
    } finally {
      setLoadingFeedback(false);
    }
  }, [feedbackStatusFilter, feedbackPriorityFilter, feedbackSearch, feedbackPage]);

  useEffect(() => {
    if (role === Roles.OWNER) {
      fetchFeedbackTickets();
    }
  }, [role, fetchFeedbackTickets]);

  const openTicketDetail = (ticket: FeedbackTicket) => {
    setSelectedTicket(ticket);
    setTicketAdminNote(ticket.adminNote || '');
    setTicketStatus(ticket.status);
    setTicketPriority(ticket.priority);
  };

  const saveTicket = async (notifyUser: boolean) => {
    if (!selectedTicket) return;
    setSavingTicket(true);
    try {
      const updated = await updateFeedbackTicket(selectedTicket._id, {
        status: notifyUser ? 'done' : ticketStatus,
        priority: ticketPriority,
        adminNote: ticketAdminNote,
      });
      setFeedbackTickets((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
      setSelectedTicket(updated);
      setCopyToast(notifyUser ? t('ticketResolvedNotify', 'Ticket resolved and user notified!') : t('ticketUpdated', 'Ticket updated'));
    } catch {
      setFeedbackError('Failed to update ticket');
    } finally {
      setSavingTicket(false);
    }
  };

  const statusColor = (status: FeedbackStatus) => {
    if (status === 'done') return 'success' as const;
    if (status === 'in_progress') return 'warning' as const;
    return 'default' as const;
  };

  const priorityColor = (priority: FeedbackPriority) => {
    if (priority === 'high') return 'error' as const;
    if (priority === 'medium') return 'warning' as const;
    return 'default' as const;
  };

  if (role !== Roles.OWNER) {
    return (
      <div className={classes.page}>
        <Typography>{t('noPermission', 'You do not have permission to access this page.')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <AdminSubpageHeader title={t('adminNavFeedbackTickets')} />
      <Paper className={classes.card} elevation={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
          <Typography className={classes.title}>
            {t('adminFeedbackTicketsTitle', 'User Feedback Tickets')}
            {feedbackTotal > 0 && <Chip size="small" label={`${feedbackTotal} total`} sx={{ ml: 1 }} />}
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} mb={1.5} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('status', 'Status')}</InputLabel>
            <Select
              value={feedbackStatusFilter}
              label={t('status', 'Status')}
              onChange={(e) => {
                setFeedbackStatusFilter(e.target.value as FeedbackStatus | '');
                setFeedbackPage(1);
              }}
            >
              <MenuItem value="">{t('all', 'All')}</MenuItem>
              <MenuItem value="open">{t('open', 'Open')}</MenuItem>
              <MenuItem value="in_progress">{t('inProgress', 'In progress')}</MenuItem>
              <MenuItem value="done">{t('done', 'Done')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('priority', 'Priority')}</InputLabel>
            <Select
              value={feedbackPriorityFilter}
              label={t('priority', 'Priority')}
              onChange={(e) => {
                setFeedbackPriorityFilter(e.target.value as FeedbackPriority | '');
                setFeedbackPage(1);
              }}
            >
              <MenuItem value="">{t('all', 'All')}</MenuItem>
              <MenuItem value="high">{t('high', 'High')}</MenuItem>
              <MenuItem value="medium">{t('medium', 'Medium')}</MenuItem>
              <MenuItem value="low">{t('low', 'Low')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder={t('feedbackSearchPlaceholder', 'Search title, email...')}
            value={feedbackSearch}
            onChange={(e) => {
              setFeedbackSearch(e.target.value);
              setFeedbackPage(1);
            }}
            sx={{ minWidth: 200 }}
          />
          <Button variant="contained" size="small" onClick={fetchFeedbackTickets} disabled={loadingFeedback}>
            {loadingFeedback ? t('loading', 'Loading...') : t('refresh', 'Refresh')}
          </Button>
        </Stack>

        {loadingFeedback ? <LinearProgress /> : null}
        {feedbackError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {feedbackError}
          </Alert>
        ) : null}

        <Stack gap={0.75}>
          {feedbackTickets.length === 0 && !loadingFeedback ? (
            <Typography color="text.secondary" fontSize={13}>
              {t('noFeedbackTickets', 'No feedback tickets found.')}
            </Typography>
          ) : null}
          {feedbackTickets.map((ticket) => (
            <Paper key={ticket._id} variant="outlined" className={classes.eventRow}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                gap={0.75}
              >
                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                  <Chip size="small" color={statusColor(ticket.status)} label={ticket.status} />
                  <Chip size="small" color={priorityColor(ticket.priority)} label={ticket.priority} />
                  <Typography fontWeight={600} fontSize={13}>
                    {ticket.title}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography className={classes.eventMeta}>{ticket.userEmail}</Typography>
                  <Typography className={classes.eventTime}>{new Date(ticket.createdAt).toLocaleDateString()}</Typography>
                  <Button size="small" variant="outlined" onClick={() => openTicketDetail(ticket)}>
                    {t('view', 'View')}
                  </Button>
                </Stack>
              </Stack>
              {ticket.pageUrl ? (
                <Typography className={classes.eventDetails} sx={{ mt: 0.5 }}>
                  {ticket.pageUrl}
                </Typography>
              ) : null}
            </Paper>
          ))}
        </Stack>

        {feedbackTotal > 20 ? (
          <Stack direction="row" gap={1} mt={1.5} alignItems="center">
            <Button size="small" disabled={feedbackPage <= 1} onClick={() => setFeedbackPage((p) => p - 1)}>
              {t('previous', 'Previous')}
            </Button>
            <Typography fontSize={12} color="text.secondary">
              {t('pageOf', {
                defaultValue: 'Page {{page}} of {{total}}',
                page: feedbackPage,
                total: Math.ceil(feedbackTotal / 20),
              })}
            </Typography>
            <Button
              size="small"
              disabled={feedbackPage >= Math.ceil(feedbackTotal / 20)}
              onClick={() => setFeedbackPage((p) => p + 1)}
            >
              {t('next', 'Next')}
            </Button>
          </Stack>
        ) : null}
      </Paper>

      <Dialog open={Boolean(selectedTicket)} onClose={() => setSelectedTicket(null)} maxWidth="sm" fullWidth>
        {selectedTicket ? (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography fontWeight={600}>
                  {t('feedbackTicketLabel', { defaultValue: 'Feedback #{{id}}', id: selectedTicket._id.slice(0, 8) })}
                </Typography>
                <Stack direction="row" gap={1}>
                  <Chip size="small" color={statusColor(selectedTicket.status)} label={selectedTicket.status} />
                  <Chip size="small" color={priorityColor(selectedTicket.priority)} label={selectedTicket.priority} />
                </Stack>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack gap={2} mt={0.5}>
                <Box>
                  <Typography fontWeight={600} fontSize={15}>
                    {selectedTicket.title}
                  </Typography>
                  <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.25 }}>
                    {t('feedbackBy', {
                      defaultValue: 'by {{email}} · {{date}}',
                      email: selectedTicket.userEmail,
                      date: new Date(selectedTicket.createdAt).toLocaleString(),
                    })}
                  </Typography>
                </Box>

                <Typography fontSize={13} sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedTicket.description}
                </Typography>

                {selectedTicket.pageUrl ? (
                  <Typography fontSize={12} color="text.secondary">
                    {t('pageLabel', 'Page')}: {selectedTicket.pageUrl}
                  </Typography>
                ) : null}

                {selectedTicket.screenshotFileId ? (
                  <Box>
                    <Typography fontSize={12} color="text.secondary" mb={0.5}>
                      {t('screenshot', 'Screenshot')}
                    </Typography>
                    <img
                      src={getFeedbackScreenshotUrl(selectedTicket._id)}
                      alt=""
                      style={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 6, border: '1px solid #eee' }}
                    />
                  </Box>
                ) : null}

                <Divider />

                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('status', 'Status')}</InputLabel>
                    <Select value={ticketStatus} label={t('status', 'Status')} onChange={(e) => setTicketStatus(e.target.value as FeedbackStatus)}>
                      <MenuItem value="open">{t('open', 'Open')}</MenuItem>
                      <MenuItem value="in_progress">{t('inProgress', 'In progress')}</MenuItem>
                      <MenuItem value="done">{t('done', 'Done')}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('priority', 'Priority')}</InputLabel>
                    <Select
                      value={ticketPriority}
                      label={t('priority', 'Priority')}
                      onChange={(e) => setTicketPriority(e.target.value as FeedbackPriority)}
                    >
                      <MenuItem value="low">{t('low', 'Low')}</MenuItem>
                      <MenuItem value="medium">{t('medium', 'Medium')}</MenuItem>
                      <MenuItem value="high">{t('high', 'High')}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                {/*
                  The conversation. Feedback used to be one-way — a note out by email with
                  no way back — so a follow-up question had nowhere to be answered.
                */}
                <Stack gap={1}>
                  <Typography variant="subtitle2">{t('conversation', 'Verlauf')}</Typography>
                  <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>
                    <FeedbackThread
                      messages={selectedTicket.messages}
                      description={selectedTicket.description}
                      viewerRole="team"
                      placeholder={t('replyToReporter', 'Antwort an den Melder')}
                      onReply={async (body) => {
                        const updated = await replyToFeedbackTicket(selectedTicket._id, body);
                        setSelectedTicket(updated);
                        await fetchFeedbackTickets();
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('replyHint', 'Wird per Mail zugestellt; der Melder kann direkt antworten.')}
                  </Typography>
                </Stack>

                <TextField
                  label={t('adminNoteLabel', 'Admin note (visible to user in notification)')}
                  value={ticketAdminNote}
                  onChange={(e) => setTicketAdminNote(e.target.value)}
                  multiline
                  minRows={3}
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 2000 }}
                />

                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button variant="outlined" onClick={() => setSelectedTicket(null)}>
                    {t('close', 'Close')}
                  </Button>
                  <Button variant="outlined" onClick={() => saveTicket(false)} disabled={savingTicket}>
                    {t('save', 'Save')}
                  </Button>
                  <Button variant="contained" color="success" onClick={() => saveTicket(true)} disabled={savingTicket}>
                    {savingTicket ? t('saving', 'Saving...') : t('resolveNotifyUser', 'Resolve & notify user')}
                  </Button>
                </Stack>
              </Stack>
            </DialogContent>
          </>
        ) : null}
      </Dialog>

      <Snackbar open={Boolean(copyToast)} autoHideDuration={1800} onClose={() => setCopyToast(null)} message={copyToast || ''} />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  page: {
    padding: '20px 16px 16px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '20px 8px 8px 8px',
    },
  },
  card: {
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: 8,
    padding: 12,
    maxWidth: 900,
  },
  title: {
    fontWeight: 600,
  },
  eventRow: {
    padding: '8px 10px',
  },
  eventTime: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  eventMeta: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
  },
  eventDetails: {
    marginTop: 4,
    fontSize: theme.typography.pxToRem(11),
    fontFamily: 'monospace',
    color: theme.palette.text.secondary,
    wordBreak: 'break-word',
  },
}));

export default AdminFeedbackTicketsPage;
